// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useMemo } from 'react';
import { LineChart } from '@carbon/charts-react';
import { ScaleTypes } from '@carbon/charts';
import type { LineChartOptions } from '@carbon/charts';
import type { MetricSeries } from '../../types';
import { useSiteTime } from '../../hooks/useSiteTime';
import './MetricLineChart.css';

export interface MetricLineChartProps {
  series: MetricSeries;
  theme: string;
}

/**
 * One Carbon line chart for a single telemetry metric series, with its own
 * title/unit label. Shared by `AlarmTelemetry` (past-24h) and the Asset
 * Topology detail panel (last-5-min) so both render identically.
 */
export default function MetricLineChart({ series, theme }: MetricLineChartProps) {
  const { zonedDate } = useSiteTime();

  // Carbon's TIME scale wants a Date; it then auto-thins ticks to fit the axis
  // width instead of drawing one label per point (which overlapped badly).
  // Carbon renders browser-local, so each point is a wall-clock stand-in, not a real instant.
  const data = useMemo(
    () => series.data.map(d => ({ group: d.group, date: zonedDate(d.key), value: d.value })),
    [series.data, zonedDate]
  );

  const options = useMemo<LineChartOptions>(
    () => ({
      // Title is rendered as our own element below — Carbon's built-in title sits
      // in a header element that doesn't pick up the dark card background.
      axes: {
        bottom: { mapsTo: 'date', scaleType: ScaleTypes.TIME },
        left: {
          mapsTo: 'value',
          title: series.unit || undefined,
          scaleType: ScaleTypes.LINEAR,
        },
      },
      timeScale: {
        // Show hour-of-day ticks (e.g. "14:00") across the window.
        timeIntervalFormats: {
          '15minutes': { primary: 'HH:mm', secondary: 'HH:mm' },
          '30minutes': { primary: 'HH:mm', secondary: 'HH:mm' },
          hourly: { primary: 'HH:mm', secondary: 'HH:mm' },
        },
      },
      curve: 'curveMonotoneX',
      points: { enabled: false },
      legend: { enabled: false },
      toolbar: { enabled: false },
      height: '240px',
      theme,
    }),
    [series.unit, theme]
  );

  return (
    <div className="metric-line-chart">
      <div className="metric-line-chart__title">
        {series.name}
        {series.unit ? <span className="metric-line-chart__unit"> ({series.unit})</span> : null}
      </div>
      <LineChart data={data} options={options} />
    </div>
  );
}
